// filepath: apps/web/src/features/admin/tier/queries.ts
//
// FR-UI-ADM-14 — TanStack Query hook for the Tier & Quota admin
// panel.
//
// Reads from a new endpoint
//   GET /api/admin/tier
// (see internal/kernel/tier_handler.go). The data is fully
// server-resolved; the SPA never recomputes tier limits or usage
// (FR-UI-NFR-2 — no client-side eval).
//
// INV-1: the cache key MUST include tenantSlug as the 2nd segment.
// Switching tenants must NOT leak the prior tenant's quota
// snapshot — tests verify the cache key shape matches
// adminKeys.matrix / adminKeys.fieldVisibility structure.
//
// State surface (FR-UI-CORE-10 AC-5 / AC-6): the hook returns a
// flat `{ data, state, error }` shape rather than TanStack's
// default `UseQueryResult` so the page maps one switch to its
// five render branches. The 5 states are
//   - loading            (initial fetch in flight)
//   - success            (TierQuotaResponse received)
//   - fetch-failure      (5xx, network, generic non-OK)
//   - permission-denied  (401 / 403 — caller lacks `audit` or `*`)
//   - rate-limit         (429 — Retry-After header surfaced)

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/api";
import type {
  TierFetchError,
  TierFetchState,
  TierQuotaResponse,
} from "./types";

/**
 * tierKeys — TanStack Query key factory for the tier panel.
 *
 *   tierKeys.all                     → ["admin", "tier"]
 *   tierKeys.forTenant(tenantSlug)   → ["admin", "tier", tenantSlug]
 *
 * INV-1: tenantSlug is the 3rd segment. Switching tenants
 * invalidates the cache automatically (different key → different
 * cache entry).
 */
export const tierKeys = {
  all: ["admin", "tier"] as const,
  forTenant: (tenantSlug: string) => [...tierKeys.all, tenantSlug] as const,
};

/**
 * UseTierQuotaResult — flat envelope the page consumes. The
 * state field is one of the 5 FR-UI-CORE-10 fetch states; the
 * page maps each to its dedicated render branch.
 */
export interface UseTierQuotaResult {
  data: TierQuotaResponse | null;
  state: TierFetchState;
  error: TierFetchError | null;
}

/**
 * useTierQuota — fetches the tenant's tier limits and usage
 * snapshot from GET /api/admin/tier.
 *
 * @param tenantSlug — resolved tenant slug (used as cache key
 *   segment AND as the implicit tenant scope; the backend reads
 *   it from the auth context but the cache key MUST include it
 *   per INV-1).
 */
export function useTierQuota(tenantSlug: string): UseTierQuotaResult {
  const q = useQuery<TierQuotaResponse, TierFetchError>({
    queryKey: tierKeys.forTenant(tenantSlug),
    queryFn: async ({ signal }) => {
      const res = await apiFetch("/api/admin/tier", {
        init: { signal },
      });
      // 401 / 403 — caller lacks `audit` or `*` admin permission.
      // The page renders the dedicated permission-denied block.
      if (res.status === 401 || res.status === 403) {
        const err: TierFetchError = {
          kind: "permission-denied",
          message: "Permission denied",
          retriable: false,
        };
        throw err;
      }
      // 429 — backpressure. Honor Retry-After; the page counts
      // down via the `retryAfterSeconds` field.
      if (res.status === 429) {
        const retryAfter = Number(res.headers.get("Retry-After") ?? "60");
        const err: TierFetchError = {
          kind: "rate-limit",
          message: "Rate limited",
          retriable: true,
          retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : 60,
        };
        throw err;
      }
      // 5xx + every other non-2xx — generic fetch failure with a
      // retry control.
      if (!res.ok) {
        const err: TierFetchError = {
          kind: "fetch-failure",
          message: `HTTP ${res.status}`,
          retriable: true,
        };
        throw err;
      }
      return (await res.json()) as TierQuotaResponse;
    },
    // The hook is meaningful only when tenantSlug is populated.
    // The TenantProvider resolves slug at boot; an empty slug
    // would be a programming error (the route shell is mounted
    // behind AuthGuard + TenantProvider).
    enabled: Boolean(tenantSlug),
    // No automatic retry — the page drives retries via the
    // "Retry" button so the user controls backoff.
    retry: false,
    // FR-UI-CORE-10 staleTime for entity data (30s).
    staleTime: 30_000,
  });

  // ── state mapping ────────────────────────────────────────────────────
  // The page reads `state` once and renders one of 5 branches.
  if (q.isPending) {
    return { data: null, state: "loading", error: null };
  }
  if (q.error) {
    return {
      data: null,
      state: q.error.kind,
      error: q.error,
    };
  }
  if (q.data) {
    return { data: q.data, state: "success", error: null };
  }
  // Defensive: neither pending, errored, nor has data — surface
  // as loading rather than silently rendering nothing.
  return { data: null, state: "loading", error: null };
}
